import { Link, useParams } from "react-router-dom";
import SEO from "../../components/SEO";
import PageHeader from "../../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../../components/Breadcrumbs";
import CTASection from "../../components/CTASection";
import CountryCompanyCard from "../../components/directory/CountryCompanyCard";
import CountryResearchOverview from "../../components/directory/CountryResearchOverview";
import LastVerifiedDate from "../../components/directory/LastVerifiedDate";
import humanoidCompanies from "../../data/humanoid-company-directory.json";
import { companies, taxonomy, companyPath } from "../../data/directories";
import { getDirectorySeo } from "../../data/directories/directorySeo";
import { itemListSchema } from "../../utils/directorySchemas";
import { slugifyDirectoryValue, sameDirectoryCompany } from "../../utils/directoryUtils";
import NotFound from "../NotFound";

const regionToContinent = {
  Asia: "Asia",
  Europe: "Europe",
  "North America": "North America",
  Oceania: "Oceania",
  "Middle East": "Asia",
};

function humanoidListSchema(country, path, entries) {
  if (!entries.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Humanoid Robot Companies in ${country}`,
    url: `https://www.techniahqservice.com${path}`,
    numberOfItems: entries.length,
    itemListElement: entries.map((company, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Organization",
        name: company.name,
        url: company.officialUrl || company.sourceUrl,
        address: company.headquarters
          ? {
              "@type": "PostalAddress",
              addressLocality: company.headquarters,
              addressCountry: company.countryCode,
            }
          : { "@type": "PostalAddress", addressCountry: company.countryCode },
      },
    })),
  };
}

export default function RoboticsCompanyCountryPage() {
  const { countrySlug } = useParams();
  const country = taxonomy.countryPages[countrySlug];
  if (!country) return <NotFound />;

  const entries = companies.filter((item) => item.country === country).sort((a, b) => a.name.localeCompare(b.name));
  const humanoidEntries = humanoidCompanies.filter((item) => item.country === country && !companies.some((company) => sameDirectoryCompany(company, item)));
  if (!entries.length && !humanoidEntries.length) return <NotFound />;

  const continent = entries[0]?.continent || regionToContinent[humanoidEntries[0]?.region] || "Asia";
  const path = `/robotics-companies/country/${countrySlug}`;
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robotics Companies", to: "/robotics-companies" },
    { label: continent, to: `/robotics-companies/${slugifyDirectoryValue(continent)}` },
    { label: country, to: path },
  ];
  const pageSeo = getDirectorySeo(path);
  const description = pageSeo.description;
  const schemas = [buildBreadcrumbSchema(breadcrumbs)];

  if (entries.length) {
    schemas.push(itemListSchema({
      name: `Robotics Companies in ${country}`,
      description,
      path,
      items: entries,
      type: "Organization",
      getPath: companyPath,
    }));
  }

  const humanoidSchema = humanoidListSchema(country, path, humanoidEntries);
  if (humanoidSchema) schemas.push(humanoidSchema);

  return <>
    <SEO title={pageSeo.title} description={description} path={path} schema={schemas} />
    <Breadcrumbs items={breadcrumbs} />
    <PageHeader eyebrow="Companies by country" title={`Robotics Companies in ${country}`} description={`Robot builders, robotics software and engineering businesses documented in ${country}.`} />
    <section className="site-shell py-8 sm:py-10">
      <CountryResearchOverview countrySlug={countrySlug} country={country} entries={entries} additionalCount={humanoidEntries.length} />

      {entries.length > 0 && <section className="mt-10">
        <h2 className="text-3xl font-black text-neutral-950">Robotics company profiles in {country}</h2>
        <p className="mt-3 max-w-3xl text-base leading-7 text-neutral-600">{entries.length} sourced profiles covering robot hardware, software and related engineering work.</p>
        <div className="mt-6 grid items-start gap-4 md:grid-cols-2">{entries.map((company) => <CountryCompanyCard key={company.id} company={company} />)}</div>
      </section>}

      {humanoidEntries.length > 0 && <section className="mt-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-700">Humanoid robotics</p>
            <h2 className="mt-2 text-3xl font-black text-neutral-950">Additional humanoid programmes in {country}</h2>
            <p className="mt-3 max-w-3xl text-base leading-7 text-neutral-600">{humanoidEntries.length} additional records from the humanoid directory, including development and historical programmes. Companies already profiled above are shown once.</p>
          </div>
          <Link to="/humanoid-robots/manufacturers" className="btn-secondary">Global humanoid company directory</Link>
        </div>
        <div className="mt-7 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {humanoidEntries.map((company) => <article key={company.id} className="soft-card soft-card-hover flex h-full flex-col p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700">{company.companyType}</p>
            <h3 className="mt-2 text-2xl font-black tracking-tight text-neutral-950">{company.name}</h3>
            {company.localName ? <p className="mt-2 text-sm font-semibold text-neutral-600">{company.localName}</p> : null}
            <p className="mt-4 text-sm leading-7 text-neutral-600">{company.robotPrograms || "Humanoid robotics program"}</p>
            <dl className="mt-5 grid gap-2 text-sm text-neutral-700">
              {company.headquarters ? <div><dt className="font-black text-neutral-950">Location</dt><dd>{company.headquarters}</dd></div> : null}
              {company.foundedYear ? <div><dt className="font-black text-neutral-950">Founded</dt><dd>{company.foundedYear}</dd></div> : null}
              <div><dt className="font-black text-neutral-950">Public status</dt><dd>{company.status}</dd></div>
            </dl>
            {company.notes ? <p className="mt-4 text-sm leading-6 text-neutral-600">{company.notes}</p> : null}
            <div className="mt-4 grid gap-2">
              <p className="text-xs leading-6 text-neutral-600">{company.verificationLevel === "official-source" ? "Company or institution source. Product claims remain attributed to that source." : "Secondary source record. Current programme status needs primary-source confirmation."}</p>
              <LastVerifiedDate date={company.lastVerifiedAt} />
            </div>
            <div className="mt-auto flex flex-wrap gap-2 pt-6">
              {company.catalogSlug ? <Link className="btn-primary" to={`/humanoid-robots/manufacturer/${company.catalogSlug}`}>View robot profile</Link> : null}
              <a className={company.catalogSlug ? "btn-secondary" : "btn-primary"} href={company.officialUrl || company.sourceUrl} target="_blank" rel="noopener noreferrer">{company.officialUrl ? "Official website" : "Source"}</a>
              {company.officialUrl && company.sourceUrl && company.sourceUrl !== company.officialUrl ? <a className="btn-secondary" href={company.sourceUrl} target="_blank" rel="noopener noreferrer">Record source</a> : null}
            </div>
          </article>)}
        </div>
      </section>}



      <div className="mt-8 flex flex-wrap gap-2"><Link to={`/robotics-companies/${slugifyDirectoryValue(continent)}`} className="btn-secondary">View {continent} companies</Link><Link to="/robotics-companies" className="btn-secondary">Global directory</Link></div>
    </section>
    <CTASection />
  </>;
}
